import { DownloadIcon, ResetIcon, UploadIcon } from '@radix-ui/react-icons';
import { Badge, Box, Flex, Grid, Text } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { CommonCallout } from 'components/common/callouts';
import { DialogButton } from 'components/common/dialogs/button';
import { CommonConfirmationDialog } from 'components/common/dialogs/confirmation';
import { CommonSearchInput } from 'components/common/form/search';
import { CommonTextareaInput } from 'components/common/form/textarea';
import { JsonDiff } from 'components/common/json/json-diff';
import { MiscHelper } from 'lib_ts/classes/misc.helper';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IOption } from 'lib_ts/interfaces/common/i-option';
import { IFirmwareWriteMsg } from 'lib_ts/interfaces/machine-msg/i-firmware-write';
import { useCallback, useEffect, useState } from 'react';
import { AdminMachinesService } from 'services/admin/machines.service';
import { useAuthStore } from 'stores/use-auth-store';

interface IProps {
  machineID: string;
}

const getPresetLabel = (path: string) => path.split('/').reverse()[0];

export const FirmwareConfigTab = ({ machineID }: IProps) => {
  const current = useAuthStore((s) => s.current);

  const [loading, setLoading] = useState(false);

  const [lastJSON, setLastJSON] = useState<string>();
  const [nextJSON, setNextJSON] = useState<string>();
  const [defaultJSON, setDefaultJSON] = useState<string>();

  const [availablePresets, setAvailablePresets] = useState<IOption[]>([]);

  const [preset, setPreset] = useState<string>();
  const [errors, setErrors] = useState<string[] | undefined>();

  const [dialogConfirm, setDialogConfirm] = useState<number>();

  const fetchConfig = useCallback(async () => {
    setLoading(true);

    const result = await AdminMachinesService.getInstance()
      .getConfig(machineID)
      .finally(() => setLoading(false));

    if (!result) {
      NotifyHelper.warning({
        message_md:
          'Received an empty result when trying to get machine config. Is the machine online?',
      });
      return;
    }

    setDefaultJSON(
      result.defaults
        ? JSON.stringify(result.defaults, null, 2)
        : 'Empty result from server'
    );

    setLastJSON(
      result.config
        ? JSON.stringify(result.config, null, 2)
        : 'Empty result from server'
    );

    setNextJSON((prev) => {
      if (result.errors && result.errors.length > 0 && prev) {
        /** leave busted config intact for correction */
        return prev;
      }

      if (result.config) {
        return JSON.stringify(result.config, null, 2);
      }

      return undefined;
    });

    setAvailablePresets(
      (result.available_presets ?? []).map((path) => ({
        label: getPresetLabel(path),
        value: path,
      }))
    );

    setErrors(result.errors);
  }, [machineID]);

  useEffect(() => {
    fetchConfig();
  }, [machineID]);

  const handleDownload = () => {
    if (!lastJSON) {
      NotifyHelper.warning({
        message_md: 'Empty value, nothing to download.',
      });
      return;
    }

    MiscHelper.saveAs(
      new Blob([JSON.stringify(lastJSON, null, 2)]),
      `${machineID}.json`
    );
  };

  const openConfirmationDialog = () => {
    if (!nextJSON) {
      NotifyHelper.warning({
        message_md: 'Update value cannot be empty.',
      });
      return;
    }

    if (nextJSON === lastJSON && !preset) {
      NotifyHelper.info({
        message_md: 'Update value is unchanged.',
      });
      return;
    }

    setDialogConfirm(Date.now());
  };

  const handleUpdate = async () => {
    if (!nextJSON) {
      return;
    }

    setLoading(true);

    // just in case the JSON is invalid
    let value: unknown;

    try {
      value = JSON.parse(nextJSON);
    } catch {
      NotifyHelper.warning({
        message_md: 'Update value is not valid JSON.',
      });
      setLoading(false);
      return;
    }

    const payload: Partial<IFirmwareWriteMsg> = {
      changedBy: current.email,
      value,
      preset: preset,
      // when no preset is selected, then overwrite the entire active value
      overwrite: !preset,
    };

    const success = await AdminMachinesService.getInstance().setMachineConfig(
      machineID,
      payload
    );

    if (!success) {
      setLoading(false);
      return;
    }

    setTimeout(() => {
      fetchConfig();
    }, 5000);
  };

  const handleReset = () => {
    setNextJSON(lastJSON);
  };

  return (
    <Flex direction="column" gap={RADIX.FLEX.GAP.MD}>
      <Grid columns="2" gap={RADIX.FLEX.GAP.MD}>
        <Box gridColumn="1">
          <CommonTextareaInput
            id="machine-config-active"
            label="Active Config"
            value={lastJSON}
            rows={10}
            monospace
            disabled
            showCopyButton
          />
        </Box>
        <Box gridColumn="2">
          <CommonTextareaInput
            id="machine-config-default"
            label="Default Config"
            value={defaultJSON}
            rows={10}
            monospace
            disabled
            showCopyButton
          />
        </Box>
      </Grid>

      <Flex direction="column" gap={RADIX.FLEX.GAP.MD}>
        <CommonTextareaInput
          id="machine-config-update"
          label="Update Value"
          name="config"
          rows={10}
          value={nextJSON}
          disabled={loading || nextJSON === undefined}
          onChange={(v) => setNextJSON(v)}
          hint_md="Some changes will only take effect after the machine is restarted (e.g. connections)."
          monospace
          showCopyButton
        />
        {errors && errors.length > 0 && (
          <CommonCallout
            color={RADIX.COLOR.DANGER}
            header="Config Errors"
            text_md={errors?.map((err) => ` - ${err}`).join('\n\n')}
          />
        )}
      </Flex>

      <Box>
        <CommonSearchInput
          id="machine-config-preset"
          name="preset"
          label="Preset"
          options={availablePresets}
          values={preset ? [preset] : []}
          onChange={(values) => setPreset(values[0])}
          disabled={loading}
          optional
        />
      </Box>

      {preset && (
        <CommonCallout
          text_md={[
            `Using  \`${
              availablePresets?.find((o) => o.value === preset)?.label
            }\` will merge its contents with the value provided above.`,
            'The contents of the preset file will take precedence if there are any conflicts.',
          ].join('\n\n')}
        />
      )}

      <Flex gap={RADIX.FLEX.GAP.SM} justify="end">
        <DialogButton
          label="common.reset"
          icon={<ResetIcon />}
          tooltip="Reset to active config"
          color={RADIX.COLOR.WARNING}
          disabled={loading}
          onClick={handleReset}
        />
        <DialogButton
          label="common.download"
          icon={<DownloadIcon />}
          disabled={loading}
          onClick={handleDownload}
        />

        <DialogButton
          label="common.update"
          icon={<UploadIcon />}
          color={RADIX.COLOR.ACCENT}
          disabled={loading}
          onClick={openConfirmationDialog}
          variant="solid"
        />
      </Flex>

      {dialogConfirm && (
        <CommonConfirmationDialog
          key={dialogConfirm}
          identifier="FWConfigUpdateConfirmation"
          title="Confirm Config Update"
          maxWidth={preset ? RADIX.DIALOG.WIDTH.MD : RADIX.DIALOG.WIDTH.XL}
          content={
            <Flex direction="column" gap={RADIX.FLEX.GAP.MD}>
              {preset ? (
                <Text>
                  You are configuring this machine to use preset{' '}
                  <Badge>{getPresetLabel(preset)}</Badge>.
                </Text>
              ) : (
                <>
                  <Text>
                    Please review your changes below before continuing.
                  </Text>
                  <JsonDiff
                    oldValue={lastJSON}
                    newValue={preset || nextJSON}
                    leftTitle="Current Config"
                    rightTitle="New Config"
                  />
                </>
              )}
              <Text>
                Please enter the ID of the machine you wish to update to confirm
                your changes.
              </Text>
            </Flex>
          }
          typeToConfirm={machineID}
          cancel={{
            label: 'common.cancel',
            color: RADIX.COLOR.DANGER,
            loading,
            onClick: () => setDialogConfirm(undefined),
          }}
          action={{
            label: 'Confirm',
            color: RADIX.COLOR.SUCCESS,
            variant: 'solid',
            loading,
            onClick: async () => {
              await handleUpdate();
              setDialogConfirm(undefined);
            },
          }}
        />
      )}
    </Flex>
  );
};
